/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines */
// 本クラスはPhotoDBOperatorを介して呼び出す前提とする

import { type IDBPDatabase, openDB } from 'idb'
import MiiaFwConfig from '@/configs/config'
import { imageCompression } from '@/accessors/indexedDB/ImageCompressor'
import { handleError } from '@/accessors/util/errorHandler'
import { v4 as uuidV4 } from 'uuid'
import type {
  Affix,
  Directory,
  Operation,
  PhotoState
} from '@/types/accessor/indexedDB/disasterEmergencyPhotoDB'
import { OPERATION } from '@/constants/accessor/indexedDB/disasterEmergencyPhotoDB'
import {
  IDB_ERROR_CODE,
  IMAGE_QUALITY,
  RAW_MAX_HEIGHT,
  RAW_MAX_WIDTH
} from '@/constants/accessor/indexedDB/disasterEmergencyPhotoDB'
import { EXPIRATION_DELTA } from '@/constants/accessor/indexedDB/indexedDB'
import { downloadObject } from '../s3/disasterEmergencyPhotoS3Accessor'

export function arrayBufferToBlob(mime: string, arrayBuffer?: ArrayBuffer): Blob {
  return new Blob([arrayBuffer === undefined ? new ArrayBuffer(0) : arrayBuffer.slice(0)], {
    type: mime
  })
}

/**
 * 画像サイズを軽量化する
 * @param value PhotoState
 * @param quality 画質 0 - 1 の少数
 * @param maxWidth 最大横幅px
 * @param maxHeight 最大縦幅px
 * @returns
 */
export const compress = async (
  file: Blob,
  quality = IMAGE_QUALITY,
  maxWidth = RAW_MAX_WIDTH,
  maxHeight = RAW_MAX_HEIGHT
): Promise<Blob> => {
  // webkitとcanvasのバグによりiOSのsafariではメモリリークが起きるため、画像圧縮はwasmを使用する
  // 参考：https://bugs.webkit.org/show_bug.cgi?id=195325
  const compressedFile = await imageCompression(file, { quality, maxWidth, maxHeight })
  return compressedFile
}

/**
 * indexedDBの管理とS3への連携を行うクラス
 * ここでは必要最低限の機能を提供する
 */
export class PhotoDB {
  /** indexedDBデータベース名 */
  private static readonly _DB_NAME: string = 'DisasterEraDB'
  private static readonly _DB_STORE_NAME: string = 'photoDB'
  /** シングルトンインスタンス */
  private static _instance: PhotoDB
  /** indexedDBクラス */
  private static _db: Promise<IDBPDatabase>

  /**
   * シングルトンインスタンスを取得する
   * @readonly
   * @param なし
   * @returns PhotoDB - インスタンス
   */
  public static get instance(): PhotoDB {
    if (!PhotoDB._instance) {
      // objectStoreの構造を変更した場合はsrc/define/config.tsのMiiaFwConfig.config.disasterEmergency.indexedDBVersion.photoDBの値を1増加させる
      const db = openDB(
        PhotoDB._DB_NAME,
        MiiaFwConfig.config.disasterEmergency.indexedDBVersion.photoDB,
        {
          upgrade(db) {
            // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度削除する
            if (db.objectStoreNames.contains(PhotoDB._DB_STORE_NAME)) {
              db.deleteObjectStore(PhotoDB._DB_STORE_NAME)
            }
            const store = db.createObjectStore(PhotoDB._DB_STORE_NAME)
            store.createIndex('fileName', 'fileName')
            store.createIndex('expirationDate', 'expirationDate')
          }
        }
      )
      PhotoDB._instance = new PhotoDB()
      PhotoDB._db = db
      return PhotoDB._instance
    }
    return PhotoDB._instance
  }

  /**
   * dbシングルトン
   */
  public static get db(): Promise<IDBPDatabase> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = PhotoDB.instance
    return PhotoDB._db
  }

  /**
   * indexedDBにPhotoStateを挿入する
   * keyPathに fileName を指定しているので、value.fileNameがないと動かない
   * @param value 挿入したいPhotoState
   * @returns PhotoDBインスタンス
   */
  public async insertItem(value: PhotoState, blob: Blob = new Blob()): Promise<Blob | null> {
    let bufferData: ArrayBuffer | undefined
    let compressedBlob: Blob | null = null
    let newPhotoState: PhotoState | null = null

    try {
      if (value.s3Operation === OPERATION.INSERT && blob.size !== 0) {
        compressedBlob = await compress(blob)
        bufferData = await compressedBlob.arrayBuffer()
      } else if (value.s3Operation === OPERATION.DELETE) {
        bufferData = new ArrayBuffer(0)
      } else {
        bufferData = value.arrayBuffer
      }

      const fileName = value.fileName.replace(/^http.*\.com\//, '')
      const extension = value.extension.replace(/\?.*$/, '')
      newPhotoState = {
        ...value,
        fileName: fileName,
        extension: extension,
        arrayBuffer: bufferData
      }
      const db = await PhotoDB.db
      const tx = db.transaction(PhotoDB._DB_STORE_NAME, 'readwrite')
      const store = tx.objectStore(PhotoDB._DB_STORE_NAME)
      const key = fileName.replace(/\//g, '')
      await store.put(newPhotoState, key)
      await tx.done
    } catch (err) {
      if (err instanceof Error) {
        await handleError(IDB_ERROR_CODE, err.message)
      } else {
        await handleError(IDB_ERROR_CODE, String(err))
      }
    } finally {
      // 明示的に大きなデータ参照を切る
      bufferData = undefined
      newPhotoState = null
      blob = null as any
    }
    // サムネイル表示に使用するため圧縮後画像を返却
    return compressedBlob
  }

  /**
   * indexedDBに指定されたPhotoStateをそのまま挿入（更新）する
   * @param value 挿入したいPhotoState
   * @returns PhotoDBインスタンス
   */
  public async upsertItem(value: PhotoState): Promise<void> {
    try {
      const db = await PhotoDB.db
      const tx = db.transaction(PhotoDB._DB_STORE_NAME, 'readwrite')
      const store = tx.objectStore(PhotoDB._DB_STORE_NAME)
      const key = value.fileName.replace(/\//g, '')
      await store.put(value, key)
      await tx.done
    } catch (err) {
      if (err instanceof Error) {
        await handleError(IDB_ERROR_CODE, err.message)
      } else {
        await handleError(IDB_ERROR_CODE, String(err))
      }
    }
  }

  /**
   * IndexedDBからPhotoStateを取得する
   * @param key S3絶対パスから拡張子をとった文字列
   * @returns PhotoState
   * @see {@link PhotoState}
   */
  public async getItem(key: string): Promise<PhotoState | undefined> {
    try {
      const db = await PhotoDB.db
      const tx = db.transaction(PhotoDB._DB_STORE_NAME, 'readonly')
      const store = tx.objectStore(PhotoDB._DB_STORE_NAME).index('fileName')
      const value = await store.get(key)
      await tx.done
      return value === undefined ? undefined : value
    } catch (err) {
      if (err instanceof Error) {
        await handleError(IDB_ERROR_CODE, err.message)
      } else {
        await handleError(IDB_ERROR_CODE, String(err))
      }
      return undefined
    }
  }

  /**
   * ステータス（s3Operation）をdeleteに更新する
   * @param key キー
   * @returns Promise<void>
   */
  public async updateStateToDelete(key: string): Promise<void> {
    const photoState = await this.getItem(key)
    if (photoState === undefined) return
    if (photoState.s3Operation !== OPERATION.DELETE) {
      const newPhotoState: PhotoState = {
        ...photoState,
        s3Operation: OPERATION.DELETE
      }
      await this.insertItem(newPhotoState)
    }
  }

  /**
   * indexedDBからkeyに対応するPhotoStateを削除する
   * @see {@link PhotoState}
   * @param keypath S3絶対パスから拡張子をとった文字列
   * @returns Promise<void>
   */
  public async deleteItem(keypath: string): Promise<void> {
    const db = await PhotoDB.db
    const tx = db.transaction(PhotoDB._DB_STORE_NAME, 'readwrite')
    const store = tx.objectStore(PhotoDB._DB_STORE_NAME)
    try {
      const key = await store.index('fileName').getKey(keypath)
      if (key !== undefined) {
        await store.delete(key)
      }
      await tx.done
    } catch (err) {
      if (err instanceof Error) {
        await handleError(IDB_ERROR_CODE, err.message)
      } else {
        await handleError(IDB_ERROR_CODE, String(err))
      }
    }
  }

  /**
   * 有効期限切れのデータを一括削除する
   * @param thresholdEpoch 削除の閾値エポック秒
   * @returns なし
   */
  public async bulkDeleteExpiredItemsFromIDB(thresholdEpoch: number = Date.now()): Promise<void> {
    let cursor = await (await PhotoDB.db)
      .transaction(PhotoDB._DB_STORE_NAME, 'readwrite')
      .objectStore(PhotoDB._DB_STORE_NAME)
      .openCursor()
    while (await cursor) {
      if (cursor == null) {
        return
      }
      // 撮影時刻 + 有効期限差分 < 現在時間だったら写真を削除する
      if (cursor.value.expirationDate + EXPIRATION_DELTA < thresholdEpoch) {
        await cursor.delete().catch(async err => {
          await handleError(IDB_ERROR_CODE, err.message)
        })
      }
      cursor = await cursor.continue()
    }
  }

  /**
   * ファイルディレクトリ名生成
   * @param directoryData ディレクトリ生成に必要なID群
   * @see {@link Directory}
   * @returns ファイルディレクトリ名 {govCode}/{disasterID}/{assessmentId}/{field}/
   */
  public generateDirectory(directoryData: Directory): string {
    return (
      [
        directoryData.govCode,
        directoryData.disasterId,
        directoryData.assessmentId,
        directoryData.field
      ].join('/') + '/'
    )
  }

  /**
   * ファイル絶対パス生成
   * @param directoryData ディレクトリ生成に必要なID群
   * @param affix ファイル名生成に必要なデータ群Affix
   * @see {@link Directory}
   * @see {@link Affix}
   * @returns ファイル絶対パス名 {govCode}/{disasterID}/{assessmentId}/{field}/0_{hash値}_{timestamp}.{extension}
   */
  public generateNewFileName(
    directoryData: Directory,
    affix: Affix
  ): { directory: string; fileName: string; extension: string } {
    const directory =
      [
        directoryData.govCode,
        directoryData.disasterId,
        directoryData.assessmentId,
        directoryData.field
      ].join('/') + '/'
    const fileName = ['0', uuidV4(), affix.timestamp].join('_')
    return { directory: directory, fileName: fileName, extension: affix.extension }
  }

  /**
   * indexedDBにおける、S3絶対パスの前方一致クエリ
   * @param key S3絶対パスから、取得したい範疇分削除した文字列
   * @return PhotoState[]
   */
  public async queryPrefixMatch(key: string): Promise<PhotoState[]> {
    const nextStr = key.slice(0, -1) + String.fromCharCode(key.slice(-1).charCodeAt(0) + 1)
    const range = IDBKeyRange.bound(key, nextStr, false, true)

    try {
      const db = await PhotoDB.db
      const tx = db.transaction(PhotoDB._DB_STORE_NAME, 'readonly')
      const store = tx.objectStore(PhotoDB._DB_STORE_NAME).index('fileName')
      const value = await store.getAll(range)
      await tx.done
      // ファイルが見つからない場合は、空を返す
      if (value === undefined || value.length < 1) {
        return []
      }
      return value
    } catch (err) {
      if (err instanceof Error) {
        await handleError(IDB_ERROR_CODE, err.message)
      } else {
        await handleError(IDB_ERROR_CODE, String(err))
      }
      return []
    }
  }

  /**
   * 複数のファイルをS3からとってきてindexedDBに挿入する
   * @param fileNameList ファイルネームリスト
   * @returns なし
   */
  public async fetchBlobFromS3AndInsertToIDB(fileNameList: string[]): Promise<void> {
    await fileNameList.reduce(async (promise, filename) => {
      return await downloadObject(filename).then(async result => {
        const blob = result as Blob
        const arrayBuffer = await blob.arrayBuffer()

        const photoData: PhotoState = {
          isOnS3: true,
          s3Operation: 'stay' as Operation,
          arrayBuffer: arrayBuffer,
          isStoredByUser: true,
          mime: blob.type,
          fileName: filename.substring(0, filename.lastIndexOf('.')),
          extension: filename.substring(filename.lastIndexOf('.')),
          expirationDate: Date.now()
        }
        if (!(await this.getItem(photoData.fileName))) {
          await this.insertItem(photoData)
        }
      })
    }, Promise.resolve())
  }

  /**
   * PKをもとにindexedDBからPhotoStateを取得する
   * @param key PK
   * @returns PhotoState
   */
  public async getItemByPrimaryKey(key: string): Promise<PhotoState | undefined> {
    try {
      const db = await PhotoDB.db
      const tx = db.transaction(PhotoDB._DB_STORE_NAME, 'readonly')
      const store = tx.objectStore(PhotoDB._DB_STORE_NAME)
      const value = await store.get(key)
      await tx.done
      // ファイルが見つからない場合は、undefinedを返す
      if (value === undefined) {
        return undefined
      }
      return value
    } catch (err) {
      if (err instanceof Error) {
        await handleError(IDB_ERROR_CODE, err.message)
      } else {
        await handleError(IDB_ERROR_CODE, String(err))
      }
    }
  }

  /**
   * indexedDBにおける、前方一致クエリ
   * @param key 絶対パスから、取得したい範疇分削除した文字列
   * @return キーリスト
   */
  public async queryPrefixMatchKeys(key: string): Promise<IDBValidKey[]> {
    const nextStr = key.slice(0, -1) + String.fromCharCode(key.slice(-1).charCodeAt(0) + 1)
    const range = IDBKeyRange.bound(key, nextStr, false, true)
    try {
      const db = await PhotoDB.db
      const tx = db.transaction(PhotoDB._DB_STORE_NAME, 'readonly')
      const store = tx.objectStore(PhotoDB._DB_STORE_NAME).index('fileName')
      const value = await store.getAllKeys(range)
      await tx.done
      return value
    } catch (err) {
      if (err instanceof Error) {
        await handleError(IDB_ERROR_CODE, err.message)
      } else {
        await handleError(IDB_ERROR_CODE, String(err))
      }
      return []
    }
  }
}
